import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router";
import { orderPath } from "../../shared/sellerPaths";
import { DELIVERY_LABEL, hasTxHash, PAYMENT_LABEL, WEEKDAY_LABEL, type MarketOrderView } from "../../shared/market";
import { formatUsd } from "../../shared/money";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError, imageUrl } from "../lib/api";
import { marketApi, type QuickReply } from "./api";
import { openMarketCart, useMarketCart } from "./cart";
import { CopyValue, Dialog, ReportButton, SellerChip, StatusPill, useArs } from "./components";
import { useInbox } from "./InboxProvider";

const POLL_MS = 15_000;

/**
 * The order for both sides: status, how to pay, steps, chat and reviews. The buyer's lives at
 * /mercado/pedido/:number; the seller's in Mi tienda (/mi-tienda/ventas/:number, `panel`). Each side
 * that opens the other's address is sent to its own.
 */
export function MarketOrderPage({ number: given, panel = false }: { number?: string; panel?: boolean } = {}) {
  const fromPath = useParams().number;
  const number = given ?? fromPath ?? "";
  const [params] = useSearchParams();
  const { search } = useLocation();
  const { t, locale } = useI18n();
  const ars = useArs();
  const auth = useAuth();
  const [order, setOrder] = useState<MarketOrderView | null>(null);
  const [error, setError] = useState<"not_found" | "other" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"cancel" | "dispute" | "ship" | null>(null);
  // Crypto: the transaction hash being typed (sent with "Ya pagué", or saved on its own after it).
  const [tx, setTx] = useState("");
  const paymentId = params.get("payment_id");
  const { carts } = useMarketCart();

  useEffect(() => {
    if (!auth.user) return;
    let alive = true;
    const load = (first: boolean) =>
      marketApi
        .order(number, first ? paymentId : null)
        .then((o) => alive && setOrder(o))
        .catch((err) => alive && first && setError(err instanceof ApiError && err.status === 404 ? "not_found" : "other"));
    void load(true);
    // New chat messages and the other side's steps show up without reloading.
    const id = window.setInterval(() => document.visibilityState === "visible" && void load(false), POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [number, auth.user?.id, paymentId]);

  // Opening the order marks it read: the badges catch up as soon as it loads or changes.
  const { refresh: refreshInbox } = useInbox();
  const status = order?.status;
  const messages = order?.messages.length;
  useEffect(() => {
    if (status) refreshInbox();
  }, [status, messages, refreshInbox]);

  const own = orderPath(number, panel ? "seller" : "buyer");
  if (!auth.loading && !auth.user) return <Navigate to={`/ingresar?next=${encodeURIComponent(own)}`} replace />;
  if (error) return <ErrorMessage message={error === "not_found" ? t("mkt.orderNotFound") : undefined} />;
  if (!order) return <Loading />;
  if ((order.role === "seller") !== panel) return <Navigate to={`${orderPath(number, order.role)}${search}`} replace />;
  const o = order;
  const seller = o.role === "seller";
  // Shipping the seller quotes in the chat: the order's total is only the cards, never "the exact amount".
  const shippingQuoted = o.deliveryCostArs === null && o.deliveryMode === "ship";
  // The buyer's cards from other sellers still in the cart: those orders get paid on their own.
  const others = seller ? [] : Object.keys(carts).filter((h) => h !== o.seller.handle && carts[h].length > 0);
  const date = (iso: string) => new Date(iso).toLocaleString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  const run = async (key: string, fn: () => Promise<MarketOrderView | { redirectUrl: string }>) => {
    setBusy(key);
    setActionError(null);
    try {
      const res = await fn();
      if ("redirectUrl" in res) window.location.assign(res.redirectUrl);
      else setOrder(res);
      setDialog(null);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      setActionError(
        code === "reason_required" ? t("mkt.reasonRequired") : code === "tx_used" ? t("mkt.txUsed") : code === "validation_error" && (key === "tx" || key === "mark_paid") ? t("mkt.txInvalid") : t("error.generic"),
      );
    } finally {
      setBusy(null);
    }
  };
  const step = (name: string, body: Record<string, unknown> = {}) => run(name, () => marketApi.step(number, name, body));
  // The hash goes along when there is one, and the box empties once it is saved.
  const withTx = (key: string, fn: () => Promise<MarketOrderView>) =>
    run(key, async () => {
      const res = await fn();
      setTx("");
      return res;
    });

  return (
    <div className="container section market-order">
      <div className="section-head">
        <div>
          <h1>{t("mkt.orderTitle", { number: o.number })}</h1>
          <p className="muted">
            {seller ? t("mkt.youSell") : t("mkt.youBuy")} · {date(o.createdAt)}
          </p>
        </div>
        <StatusPill status={o.status} />
      </div>

      <StatusBanner order={o} />

      <div className="order-layout">
        <div className="stack">
          {o.payTo && (
            <section className="pay-box" aria-labelledby="pay-title">
              <h2 id="pay-title" className="small-title">
                {t("mkt.howToPay")}
              </h2>
              <p>
                {t(shippingQuoted ? "mkt.payPlusShipping" : "mkt.payDirect", {
                  total: o.payUsdt ? `${o.payUsdt} USDT` : o.paymentMethod === "crypto" && o.totalUsdCents ? formatUsd(o.totalUsdCents, locale) : ars(o.totalArs),
                  handle: o.seller.handle,
                  name: o.seller.displayName,
                  number: o.number,
                })}
              </p>
              {o.payTo.transfer && (
                <div className="stack-sm">
                  <CopyValue label={t(shippingQuoted ? "mkt.amountNoShipping" : "mkt.exactAmount")} value={String(o.totalArs)} display={ars(o.totalArs)} className="copy-amount" />
                  <CopyValue label={t("mkt.holder")} value={o.payTo.transfer.holder} />
                  {o.payTo.transfer.cbu && <CopyValue label="CBU / CVU" value={o.payTo.transfer.cbu} />}
                  {o.payTo.transfer.alias && <CopyValue label="Alias" value={o.payTo.transfer.alias} />}
                  {o.payTo.transfer.bank && <CopyValue label={t("mkt.bank")} value={o.payTo.transfer.bank} />}
                  <p className="small muted">{t("mkt.checkHolder", { holder: o.payTo.transfer.holder })}</p>
                </div>
              )}
              {o.payUsdt && (
                <div className="stack-sm">
                  <CopyValue label={t("mkt.exactAmount")} value={o.payUsdt} display={`${o.payUsdt} USDT`} className="copy-amount" />
                  <p className="small muted">{t(o.payTo.wallets[0]?.network === "TRON (TRC-20)" ? "mkt.usdtExactAuto" : "mkt.usdtExact")}</p>
                </div>
              )}
              {o.payTo.wallets.map((w) => (
                <CopyValue key={w.address} label={`${w.asset} · ${w.network}`} value={w.address} />
              ))}
              {o.paymentMethod === "crypto" && o.payTo.wallets.some((w) => hasTxHash(w.network)) && (
                <div className="field">
                  <label htmlFor="pay-tx">{t("mkt.txLabel")}</label>
                  <input id="pay-tx" value={tx} onChange={(e) => setTx(e.target.value)} autoCapitalize="none" autoComplete="off" spellCheck={false} />
                  <span className="field-hint">{t("mkt.txHint")}</span>
                  {o.buyerMarkedPaidAt && (
                    <div>
                      <button type="button" className="btn btn-outline btn-sm" disabled={busy !== null || !tx.trim()} onClick={() => withTx("tx", () => marketApi.paymentTx(number, tx))}>
                        {t(o.paymentTx ? "mkt.txReplace" : "mkt.txSave")}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {others.length > 0 && (
                <p className="notice notice-warn small">
                  {t("mkt.payOnlyThis", { handle: o.seller.handle, others: others.map((h) => `@${h}`).join(", ") })}{" "}
                  <button type="button" className="btn btn-link btn-sm" onClick={openMarketCart}>
                    {t("mkt.seeCart")}
                  </button>
                </p>
              )}
              <p className="notice notice-warn small">{t("mkt.manualWarning")}</p>
              {o.reservedUntil && !o.buyerMarkedPaidAt && <p className="muted small">{t("mkt.reservedUntil", { date: date(o.reservedUntil) })}</p>}
            </section>
          )}

          {o.paymentTx && (
            <p className="pay-tx small">
              {t("mkt.txShown")} <code title={o.paymentTx.hash}>{`${o.paymentTx.hash.slice(0, 10)}…${o.paymentTx.hash.slice(-8)}`}</code>
              {o.paymentTx.url && (
                <>
                  {" · "}
                  <a href={o.paymentTx.url} target="_blank" rel="noopener noreferrer">
                    {t("mkt.txExplorer")} ↗
                  </a>
                </>
              )}
              {o.paymentTx.check && o.status === "pending_payment" && (
                <span className={`tx-check ${o.paymentTx.check === "pending" ? "muted" : "tx-check-bad"}`}>{t(`mkt.txCheck.${o.paymentTx.check}` as MessageKey)}</span>
              )}
            </p>
          )}
          {seller && o.status === "pending_payment" && o.paymentMethod === "crypto" && o.buyerMarkedPaidAt && (
            <p className="muted small">{!o.paymentTx ? t("mkt.txMissingSeller") : o.payUsdt ? t("mkt.txCheckSellerAmount", { usdt: o.payUsdt }) : t("mkt.txCheckSeller")}</p>
          )}
          {o.actions.length > 0 && (
            <div className="order-actions">
              {o.actions.includes("pay_mp") && (
                <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => run("pay", () => marketApi.pay(number))}>
                  {t("mkt.payWithMp")}
                </button>
              )}
              {o.actions.includes("mark_paid") && (
                <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => withTx("mark_paid", () => marketApi.step(number, "mark_paid", tx.trim() ? { tx } : {}))}>
                  {t("mkt.markPaid")}
                </button>
              )}
              {o.actions.includes("confirm_payment") && (
                <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => step("confirm_payment")}>
                  {o.buyerMarkedPaidAt ? t("mkt.confirmPaymentMarked") : t("mkt.confirmPayment")}
                </button>
              )}
              {o.actions.includes("ship") && (
                <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => setDialog("ship")}>
                  {t("mkt.markShipped")}
                </button>
              )}
              {o.actions.includes("handed") && (
                <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => step("handed")}>
                  {t("mkt.markHanded")}
                </button>
              )}
              {o.actions.includes("received") && (
                <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => step("received")}>
                  {o.status === "confirmed" ? t("mkt.markReceivedCash") : t("mkt.markReceived")}
                </button>
              )}
              {o.actions.includes("dispute") && (
                <button type="button" className="btn btn-outline" onClick={() => setDialog("dispute")}>
                  {t("mkt.askHelp")}
                </button>
              )}
              {o.actions.includes("cancel") && (
                <button type="button" className="btn btn-link" onClick={() => setDialog("cancel")}>
                  {t("mkt.cancelOrder")}
                </button>
              )}
            </div>
          )}
          {actionError && <p className="form-error" role="alert">{actionError}</p>}

          <Chat order={o} onSent={setOrder} />

          {(o.actions.includes("review") || o.myReview || o.theirReview) && <ReviewBox order={o} onDone={setOrder} />}
        </div>

        <aside className="stack">
          <section className="order-card">
            <ul className="mc-lines">
              {o.lines.map((l) => (
                <li key={l.listingId} className="mc-line">
                  {l.image && <img src={imageUrl(l.image)} alt="" width={48} height={48} />}
                  <div>
                    <Link to={`/mercado/${l.listingId}`}>{l.title}</Link>
                    <div className="muted small">
                      {l.condition} · {l.quantity} × {ars(l.unitPriceArs)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <dl className="totals">
              <dt>{t("mkt.subtotal")}</dt>
              <dd>{ars(o.subtotalArs)}</dd>
              {o.discountArs - (o.coupon?.discountArs ?? 0) > 0 && (
                <>
                  <dt>{o.coupon ? t("mkt.bulkPart") : t("mkt.discount")}</dt>
                  <dd>− {ars(o.discountArs - (o.coupon?.discountArs ?? 0))}</dd>
                </>
              )}
              {o.coupon && o.coupon.discountArs > 0 && (
                <>
                  <dt>{t("mkt.couponLine", { code: o.coupon.code })}</dt>
                  <dd>− {ars(o.coupon.discountArs)}</dd>
                </>
              )}
              <dt>{DELIVERY_LABEL[o.deliveryMode][locale]}</dt>
              <dd>{o.deliveryCostArs === null ? t("mkt.toAgree") : o.deliveryCostArs === 0 ? t("mkt.free") : ars(o.deliveryCostArs)}</dd>
              <dt className="total">{t("mkt.total")}</dt>
              <dd className="total">{ars(o.totalArs)}</dd>
            </dl>
            <p className="muted small">{PAYMENT_LABEL[o.paymentMethod][locale]}</p>
          </section>

          <section className="order-card stack-sm">
            {seller ? (
              <>
                <h2 className="small-title">{t("mkt.buyer")}</h2>
                <p>
                  <strong>{o.buyer.name}</strong>
                  {o.buyer.ratingCount > 0 && <span className="muted small"> · ★ {o.buyer.ratingAvg} ({o.buyer.ratingCount})</span>}
                </p>
                {o.buyer.phone ? <p>📞 {o.buyer.phone}</p> : <p className="muted small">{t("mkt.contactAfterPay")}</p>}
                {o.buyer.email && <p>✉️ <a href={`mailto:${o.buyer.email}`}>{o.buyer.email}</a></p>}
                {o.buyer.address && <p>{[o.buyer.address, o.buyer.city].filter(Boolean).join(", ")}</p>}
                {o.buyer.notes && <p className="pre-line small">{o.buyer.notes}</p>}
              </>
            ) : (
              <>
                <h2 className="small-title">{t("mkt.seller")}</h2>
                <SellerChip seller={o.seller} />
                {o.sellerContact ? (
                  <>
                    <p>📞 {o.sellerContact.phone}</p>
                    {o.sellerContact.email && (
                      <p>
                        ✉️ <a href={`mailto:${o.sellerContact.email}`}>{o.sellerContact.email}</a>
                      </p>
                    )}
                  </>
                ) : (
                  <p className="muted small">{t("mkt.contactAfterPay")}</p>
                )}
              </>
            )}
            {o.meeting && (
              <p className="meeting">
                📅 {(() => {
                  const d = new Date(`${o.meeting.date}T12:00:00Z`);
                  return t("mkt.meetingLine", { day: `${WEEKDAY_LABEL[d.getUTCDay()][locale]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}`, from: o.meeting.from, to: o.meeting.to });
                })()}
              </p>
            )}
            {o.paymentMethod === "cash" && o.status === "confirmed" && <p className="small">💵 {t("mkt.cashToPay", { total: ars(o.totalArs) })}</p>}
            {o.pickup && (
              <p>
                📍 {o.pickup.address}
                {o.pickup.hours ? ` · ${o.pickup.hours}` : ""}
              </p>
            )}
            {o.tracking && <p>{t("mkt.tracking", { code: o.tracking })}</p>}
          </section>
          <ReportButton target="order" targetId={o.number} label={t("mkt.reportOrder")} />
        </aside>
      </div>

      {dialog && <StepDialog kind={dialog} busy={busy !== null} onClose={() => setDialog(null)} onConfirm={(text) => step(dialog === "ship" ? "ship" : dialog, dialog === "ship" ? { tracking: text } : { reason: text })} />}
    </div>
  );
}

function StatusBanner({ order: o }: { order: MarketOrderView }) {
  const { t, locale } = useI18n();
  const seller = o.role === "seller";
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "long" });
  const text =
    o.status === "confirmed"
      ? seller
        ? t("mkt.st.confirmedSeller")
        : t("mkt.st.confirmedBuyer")
      : o.status === "pending_payment"
      ? o.buyerMarkedPaidAt
        ? seller
          ? t("mkt.st.markedSeller")
          : t("mkt.st.markedBuyer")
        : seller
          ? t("mkt.st.unpaidSeller")
          : o.paymentMethod === "mercadopago"
            ? t("mkt.st.unpaidMp")
            : t("mkt.st.unpaidBuyer")
      : o.status === "paid"
        ? seller
          ? t("mkt.st.paidSeller")
          : t("mkt.st.paidBuyer")
        : o.status === "shipped"
          ? seller
            ? t("mkt.st.shippedSeller", { date: o.autoCompleteAt ? fmt(o.autoCompleteAt) : "" })
            : t("mkt.st.shippedBuyer", { date: o.autoCompleteAt ? fmt(o.autoCompleteAt) : "" })
          : o.status === "completed"
            ? t("mkt.st.completed")
            : o.status === "disputed"
              ? t("mkt.st.disputed")
              : t("mkt.st.cancelled", { reason: o.cancelReason ?? "" });
  return <p className={`order-banner order-banner-${o.status}`}>{text}</p>;
}

function Chat({ order, onSent }: { order: MarketOrderView; onSent: (o: MarketOrderView) => void }) {
  const { t, locale } = useI18n();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  // The shop's saved answers (plans with them), dropped into the box: never sent by themselves.
  const [quick, setQuick] = useState<QuickReply[]>([]);
  useEffect(() => {
    if (order.role !== "seller") return;
    let alive = true;
    marketApi
      .quickReplies()
      .then((r) => alive && setQuick(r.replies))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [order.role]);
  const end = useRef<HTMLLIElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "nearest" });
  }, [order.messages.length]);
  const closed = order.status === "cancelled" && order.cancelledAt !== null && Date.now() - new Date(order.cancelledAt).getTime() > 30 * 86_400_000;
  return (
    <section className="chat" aria-labelledby="chat-title">
      <h2 id="chat-title" className="small-title">
        {t("mkt.chat")}
      </h2>
      <p className="muted small">{t("mkt.chatSafety")}</p>
      <ol className="chat-list" aria-live="polite">
        {order.messages.map((m) => (
          <li key={m.id} className={m.system ? "chat-msg is-system" : m.mine ? "chat-msg is-mine" : "chat-msg"}>
            <span className="pre-line">{m.body}</span>
            <time className="muted small" dateTime={m.createdAt}>
              {new Date(m.createdAt).toLocaleString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </time>
          </li>
        ))}
        <li ref={end} aria-hidden="true" />
      </ol>
      {!closed && (
        <form
          className="chat-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!text.trim()) return;
            setSending(true);
            setFailed(false);
            try {
              onSent(await marketApi.message(order.number, text));
              setText("");
            } catch {
              setFailed(true);
            } finally {
              setSending(false);
            }
          }}
        >
          <label htmlFor="chat-text" className="sr-only">
            {t("mkt.chatLabel")}
          </label>
          {quick.length > 0 && (
            <p className="quick-replies small">
              <span className="muted">{t("qr.inChat")}</span>{" "}
              {quick.map((r) => (
                <button key={r.id} type="button" className="chip" onClick={() => setText((x) => (x.trim() ? `${x.trimEnd()}\n${r.body}` : r.body))}>
                  {r.title}
                </button>
              ))}
            </p>
          )}
          <textarea id="chat-text" rows={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("mkt.chatPlaceholder")} />
          <button type="submit" className="btn btn-primary" disabled={sending || !text.trim()}>
            {t("mkt.send")}
          </button>
        </form>
      )}
      {failed && <p className="form-error">{t("error.generic")}</p>}
    </section>
  );
}

function ReviewBox({ order: o, onDone }: { order: MarketOrderView; onDone: (o: MarketOrderView) => void }) {
  const { t } = useI18n();
  const [stars, setStars] = useState(0);
  const [body, setBody] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const target = o.role === "buyer" ? t("mkt.theSeller") : t("mkt.theBuyer");
  return (
    <section className="review-box stack-sm" aria-labelledby="review-title">
      <h2 id="review-title" className="small-title">
        {t("mkt.reviewTitle")}
      </h2>
      {o.myReview ? (
        <p>
          {t("mkt.yourReview")} {"★".repeat(o.myReview.stars)} {o.myReview.body}
        </p>
      ) : (
        o.actions.includes("review") && (
          <form
            className="stack-sm"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!stars) return;
              setBusy(true);
              try {
                onDone(await marketApi.review(o.number, stars, body));
              } finally {
                setBusy(false);
              }
            }}
          >
            <fieldset className="star-input">
              <legend>{t("mkt.rate", { who: target })}</legend>
              {[1, 2, 3, 4, 5].map((n) => (
                <label key={n} className={n <= stars ? "is-on" : undefined}>
                  <input type="radio" name="stars" value={n} checked={stars === n} onChange={() => setStars(n)} />
                  <span aria-hidden="true">★</span>
                  <span className="sr-only">{t("mkt.starsShort", { count: n })}</span>
                </label>
              ))}
            </fieldset>
            <textarea aria-label={t("mkt.reviewText")} rows={2} maxLength={1000} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("mkt.reviewPlaceholder")} />
            <button type="submit" className="btn btn-primary" disabled={!stars || busy}>
              {t("mkt.reviewSend")}
            </button>
          </form>
        )
      )}
      {o.theirReview && (
        <div>
          <p>
            {t("mkt.theirReview", { who: target })} {"★".repeat(o.theirReview.stars)} {o.theirReview.body}
          </p>
          {o.theirReview.reply ? (
            <p className="review-reply">{o.theirReview.reply}</p>
          ) : (
            o.actions.includes("reply") && (
              <form
                className="chat-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!reply.trim()) return;
                  onDone(await marketApi.reply(o.number, reply));
                }}
              >
                <textarea aria-label={t("mkt.replyLabel")} rows={2} maxLength={1000} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("mkt.replyPlaceholder")} />
                <button type="submit" className="btn btn-outline">
                  {t("mkt.replySend")}
                </button>
              </form>
            )
          )}
        </div>
      )}
    </section>
  );
}

function StepDialog({ kind, busy, onClose, onConfirm }: { kind: "cancel" | "dispute" | "ship"; busy: boolean; onClose: () => void; onConfirm: (text: string) => void }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const copy = {
    cancel: { title: t("mkt.cancelTitle"), label: t("mkt.cancelReason"), button: t("mkt.cancelOrder"), intro: t("mkt.cancelIntro") },
    dispute: { title: t("mkt.disputeTitle"), label: t("mkt.disputeReason"), button: t("mkt.askHelp"), intro: t("mkt.disputeIntro") },
    ship: { title: t("mkt.shipTitle"), label: t("mkt.trackingLabel"), button: t("mkt.markShipped"), intro: t("mkt.shipIntro") },
  }[kind];
  return (
    <Dialog title={copy.title} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm(text);
        }}
      >
        <p className="muted small">{copy.intro}</p>
        <div className="field">
          <label htmlFor="step-text">{copy.label}</label>
          {kind === "ship" ? <input id="step-text" value={text} onChange={(e) => setText(e.target.value)} maxLength={120} /> : <textarea id="step-text" rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} required={kind === "dispute"} />}
        </div>
        <button type="submit" className={kind === "cancel" ? "btn btn-outline" : "btn btn-primary"} disabled={busy}>
          {copy.button}
        </button>
      </form>
      <button type="button" className="btn btn-link" onClick={onClose}>
        {t("nav.close")}
      </button>
    </Dialog>
  );
}
